import React from 'react';
import {
  CL, Easing, T, env, rise, kf,
  DISPLAY, BODY, MONO, SOFT, INK, RED, GREEN, DIM, MUT, ACCENT, GRAD,
  TimeContext, useTime,
  Frame, Radar as RadarBase, PulseArcs, Rings, Cane, SideCane, Silhouette,
} from './shared';

/* ClearWay Explainer — cinematic hero animation, desktop 16:9 stage.
 *
 * Ported from the Claude Design handoff (`ClearWay Explainer.dc.html` /
 * clearway-scene.jsx). The original ran inside a standalone timeline
 * "Stage" harness built for the design tool's scrubber/export chrome;
 * here the scene tree is driven by a plain `time` (seconds) prop from
 * <ExplainerHero>, so the whole thing is a pure function of time and
 * has no internal animation loop of its own.
 *
 * Design canvas is a fixed 1920x1080 stage — the parent is responsible
 * for scaling/centering it responsively. The 1080x1920 portrait
 * recomposition of the same story lives in ClearWayExplainerMobile.
 */

export { EXPLAINER_DURATION, EXPLAINER_SCENES } from './shared';

const W = 1920, H = 1080;
const E = Easing;

const Radar = (props) => <RadarBase w={W} h={H} cx={960} cy={540} maxR={640} base={50} {...props} />;

// ── Stage-format atoms ────────────────────────────────────────────────
function Cap({ t0, t1, text, size = 92, y = 150, maxW = 1560, color, useGrad = true, weight = 700, track = '-0.03em' }) {
  const t = useTime();
  const o = env(t, t0, 0.7, t1, 0.5);
  if (o <= 0) return null;
  const dy = rise(t, t0, 0.9, 36);
  return (
    <div style={{
      position: 'absolute', left: '50%', top: y, transform: `translate(-50%,${dy}px)`,
      width: maxW, textAlign: 'center', fontFamily: DISPLAY, fontWeight: weight,
      fontSize: size, letterSpacing: track, lineHeight: 1.06, opacity: o,
      ...(useGrad && !color ? GRAD : { color: color || INK }),
    }}>{text}</div>
  );
}

function Pill({ t0, t1, y, text }) {
  const t = useTime();
  const o = env(t, t0, 0.6, t1, 0.4);
  if (o <= 0) return null;
  return (
    <div style={{
      position: 'absolute', left: '50%', top: y,
      transform: `translateX(-50%) translateY(${rise(t, t0, 0.8, 20)}px)`, opacity: o,
      display: 'inline-flex', alignItems: 'center', gap: 10, padding: '9px 20px',
      borderRadius: 9999, border: '1px solid rgba(59,130,246,0.25)',
      background: 'rgba(59,130,246,0.07)', color: SOFT, fontFamily: BODY,
      fontSize: 16, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: SOFT, boxShadow: `0 0 10px ${SOFT}` }} />
      {text}
    </div>
  );
}

function Chip({ x, y, o = 1, tone = 'accent', label, value }) {
  const colors = {
    accent: { b: 'rgba(59,130,246,0.3)', bg: 'rgba(59,130,246,0.08)', c: SOFT },
    danger: { b: 'rgba(248,113,113,0.4)', bg: 'rgba(239,68,68,0.10)', c: RED },
    ok: { b: 'rgba(52,211,153,0.35)', bg: 'rgba(52,211,153,0.08)', c: GREEN },
  }[tone];
  return (
    <div style={{
      position: 'absolute', left: x, top: y, transform: 'translateX(-50%)', opacity: o,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
      padding: '12px 22px', borderRadius: 16, border: `1px solid ${colors.b}`,
      background: colors.bg, backdropFilter: 'blur(12px)',
    }}>
      <span style={{ fontFamily: BODY, fontSize: 13, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.c }}>{label}</span>
      {value && <span style={{ fontFamily: MONO, fontSize: 34, fontWeight: 600, color: INK, fontVariantNumeric: 'tabular-nums' }}>{value}</span>}
    </div>
  );
}

// ── Scene 1 · Intro ─────────────────────────────────────────────────
function Intro() {
  const t = useTime();
  if (t > T.problem + 0.2) return null;
  return (
    <Frame t0={0} t1={T.problem} zoomFrom={1.05} zoomTo={1} fadeIn={0.9}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(700px 500px at 50% 50%, rgba(59,130,246,0.07), transparent 70%)' }} />
      <Radar t={t} alpha={0.3} />
      <Pill t0={0.7} t1={5.9} y={356} text="Introducing" />
      <Cap t0={1.3} t1={6.0} size={184} y={430} text="ClearWay" />
      <Cap t0={2.4} t1={6.0} size={34} y={668} useGrad={false} color={MUT} weight={500} track="-0.01em" text="The smart mobility stick." />
    </Frame>
  );
}

// ── Scene 2 · The problem ──────────────────────────────────────────
function FloorGrid({ t, opacity = 1 }) {
  const shift = (t * 60) % 90;
  return (
    <div style={{
      position: 'absolute', left: -300, right: -300, top: 540, height: 700,
      transform: 'perspective(720px) rotateX(58deg)', transformOrigin: '50% 0%',
      backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(0deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
      backgroundSize: '90px 90px', backgroundPosition: `0px ${shift}px`, opacity,
      maskImage: 'linear-gradient(180deg, transparent 0%, #000 20%, #000 72%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 20%, #000 72%, transparent 100%)',
    }} />
  );
}

function Problem() {
  const t = useTime();
  if (t < T.problem - 0.3 || t > T.reveal + 0.2) return null;
  const lt = t - T.problem;
  return (
    <Frame t0={T.problem} t1={T.reveal} zoomFrom={1} zoomTo={1.09} origin="50% 70%">
      <FloorGrid t={lt} opacity={0.75} />
      <Silhouette lt={lt} delay={0.9} x={400} y={560} w={220} h={330} />
      <Silhouette lt={lt} delay={1.7} x={1310} y={520} w={260} h={370} />
      <Silhouette lt={lt} delay={2.7} x={860} y={690} w={330} h={200} />
      <Cap t0={T.problem + 0.8} t1={T.problem + 4.7} size={96} y={260} text="Every step is a guess." />
      <Cap t0={T.problem + 5.1} t1={T.reveal - 0.4} size={84} y={230}
        text={<span>Obstacles you can't see.<br />Drops you can't predict.</span>} />
    </Frame>
  );
}

// ── Scene 3 · Reveal ────────────────────────────────────────────────
// (x, y) is the anchor dot's centre — the exact stage point the callout
// points at; the leader line and text grow outward on the chosen side.
function Callout({ lt, at, endLt, x, y, side, title, sub }) {
  const o = env(lt, at, 0.6, endLt, 0.4);
  if (o <= 0) return null;
  const lw = E.easeOutCubic(CL((lt - at) / 0.7, 0, 1)) * 96;
  const textAlign = side === 'right' ? 'left' : 'right';
  return (
    <div style={{
      position: 'absolute', top: y, transform: 'translateY(-50%)', opacity: o,
      ...(side === 'right' ? { left: x - 4 } : { right: W - x - 4 }),
      display: 'flex', alignItems: 'center', gap: 0,
      flexDirection: side === 'right' ? 'row' : 'row-reverse',
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: SOFT, boxShadow: `0 0 12px ${SOFT}`, flex: 'none' }} />
      <span style={{ width: lw, height: 1, background: 'rgba(96,165,250,0.5)', flex: 'none' }} />
      <div style={{ padding: '0 16px', textAlign, minWidth: 300 }}>
        <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 27, color: INK, letterSpacing: '-0.015em' }}>{title}</div>
        <div style={{ fontFamily: BODY, fontSize: 19, color: DIM, marginTop: 3 }}>{sub}</div>
      </div>
    </div>
  );
}

function Reveal() {
  const t = useTime();
  if (t < T.reveal - 0.3 || t > T.forward + 0.2) return null;
  const lt = t - T.reveal;
  const caneY = 1120 - E.easeOutQuart(CL(lt / 1.8, 0, 1)) * 950;
  const sweep = CL((lt - 1.6) / 1.5, 0, 1);
  const glow = env(t, T.reveal + 1.4, 1.6, T.forward, 0.8);
  const endLt = T.forward - T.reveal - 0.4;
  return (
    <Frame t0={T.reveal} t1={T.forward} zoomFrom={1} zoomTo={1.07} origin="50% 60%">
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(620px 720px at 50% 62%, rgba(59,130,246,0.13), transparent 70%)', opacity: glow }} />
      <Radar cx={960} cy={646} t={lt} alpha={0.13} maxR={520} dot={false} />
      {/* floor reflection puddle */}
      <div style={{ position: 'absolute', left: 660, top: 940, width: 600, height: 60, borderRadius: '50%', background: 'radial-gradient(closest-side, rgba(59,130,246,0.10), transparent)', opacity: glow }} />
      <div style={{ position: 'absolute', left: 880, top: caneY }}>
        <Cane h={780} glow={0.65 * glow} sweep={sweep} />
      </div>
      <Cap t0={T.reveal + 1.1} t1={T.reveal + 4.3} size={112} y={96} text="Meet ClearWay." />
      {/* Anchors derived from the settled cane: wrapper (880,170) + Cane h=780
        * geometry — sensor module right edge x=1008 / upper transducer y=659,
        * red sleeve left edge x=952 / visible centre y=837, bare shaft left
        * edge x=953 between grip and first collar. */}
      <Callout lt={lt} at={4.6} endLt={endLt} x={1020} y={659} side="right" title="Dual smart sensors" sub="Forward + downward scanning" />
      <Callout lt={lt} at={5.4} endLt={endLt} x={940} y={837} side="left" title="High-visibility marker" sub="Recognised worldwide" />
      <Callout lt={lt} at={6.2} endLt={endLt} x={941} y={408} side="left" title="Feather-light shaft" sub="Folds to fit in a bag" />
    </Frame>
  );
}

// ── Scene 4 · Forward detection (sensors) ────────────────────────────
function Floor({ y = 900 }) {
  return (
    <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '100%', pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: y, height: 2, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.16) 20%, rgba(255,255,255,0.16) 80%, transparent)' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, top: y + 2, bottom: 0, background: 'linear-gradient(180deg, rgba(255,255,255,0.035), transparent 70%)' }} />
    </div>
  );
}

function Forward() {
  const t = useTime();
  if (t < T.forward - 0.3 || t > T.down + 0.2) return null;
  const lt = t - T.forward;
  const caneX = kf([1, 6.2, 7.0], [240, 990, 998], E.easeInOutQuad)(lt);
  const alerted = lt >= 6.2;
  const emit = { x: caneX + 64, y: 756 };
  const obsL = 1490;
  const distM = Math.max((obsL - emit.x) / 440, 0);
  const flash = 0.3 + Math.max(0, Math.sin(lt * 5.5)) * 0.35;
  return (
    <Frame t0={T.forward} t1={T.down} zoomFrom={1.04} zoomTo={1.1} origin="62% 66%">
      <Floor y={900} />
      {/* obstacle */}
      <div style={{
        position: 'absolute', left: obsL, top: 640, width: 160, height: 260, borderRadius: 14,
        background: 'linear-gradient(180deg, #1d1e22, #131418)',
        border: '1px solid rgba(255,255,255,0.09)',
        boxShadow: `0 30px 60px rgba(0,0,0,0.55)${lt > 2.4 ? `, -18px 0 44px rgba(59,130,246,${flash * 0.35})` : ''}`,
      }} />
      {/* echo shimmer on obstacle face */}
      {lt > 2.4 && !alerted && (
        <div style={{ position: 'absolute', left: obsL - 2, top: 640, width: 4, height: 260, borderRadius: 3, background: SOFT, opacity: flash * 0.7, filter: 'blur(1px)' }} />
      )}
      <SideCane x={caneX} y={900} t={lt} walking={!alerted} />
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', left: 0, top: 0 }}>
        <PulseArcs cx={emit.x} cy={emit.y} t={lt} startT={1.6} dir={-4} spread={56}
          maxR={Math.min(560, obsL - emit.x + 30)} color={alerted ? RED : ACCENT} alpha={alerted ? 0.9 : 0.7} period={alerted ? 0.65 : 1.05} />
        {alerted && <Rings cx={caneX - 138} cy={292} t={lt} startT={6.25} color={SOFT} maxR={150} />}
      </svg>
      {/* distance readout */}
      {lt > 2.2 && (
        <Chip x={1240} y={368} o={env(lt, 2.2, 0.6, T.down - T.forward - 0.4, 0.4)}
          tone={alerted ? 'danger' : 'accent'}
          label={alerted ? 'Obstacle — alert' : 'Scanning ahead'}
          value={distM.toFixed(2) + ' m'} />
      )}
      <Cap t0={T.forward + 0.7} t1={T.forward + 4.6} size={88} y={120} text="Sees what you can't." />
      <Cap t0={T.forward + 6.7} t1={T.down - 0.4} size={88} y={120} text="Alerts you in time to stop." />
    </Frame>
  );
}

// ── Scene 5 · Downward scanning ─────────────────────────────────────
function Downward() {
  const t = useTime();
  if (t < T.down - 0.3 || t > T.alert + 0.2) return null;
  const lt = t - T.down;
  const upperY = 840, lowerY = 1010, edgeX = 1310;
  const caneX = kf([0.8, 5.2, 6.0], [300, 1160, 1170], E.easeInOutQuad)(lt);
  const emit = { x: caneX + 64, y: 696 };
  const dirRad = 38 * Math.PI / 180;
  const scanX = emit.x + (upperY - emit.y) / Math.tan(dirRad);
  const overEdge = scanX > edgeX;
  const alerted = overEdge && lt > 1.5;
  const hit = alerted
    ? { x: emit.x + (lowerY - emit.y) / Math.tan(dirRad), y: lowerY }
    : { x: scanX, y: upperY };
  const beamOn = lt > 1.4;
  return (
    <Frame t0={T.down} t1={T.alert} zoomFrom={1.03} zoomTo={1.1} origin="60% 70%">
      {/* two-level floor */}
      <div style={{ position: 'absolute', left: 0, width: edgeX, top: upperY, height: 2, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.16) 15%, rgba(255,255,255,0.18))' }} />
      <div style={{ position: 'absolute', left: edgeX, top: upperY, width: 2, height: lowerY - upperY, background: 'rgba(255,255,255,0.16)' }} />
      <div style={{ position: 'absolute', left: edgeX, right: 0, top: lowerY, height: 2, background: 'rgba(255,255,255,0.14)' }} />
      <div style={{ position: 'absolute', left: 0, width: edgeX, top: upperY + 2, height: 168, background: 'linear-gradient(180deg, rgba(255,255,255,0.03), transparent)' }} />
      <div style={{ position: 'absolute', left: edgeX, right: 0, top: lowerY + 2, bottom: 0, background: 'linear-gradient(180deg, rgba(255,255,255,0.03), transparent)' }} />
      <SideCane x={caneX} y={upperY} t={lt} walking={!alerted} />
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', left: 0, top: 0 }}>
        {beamOn && (
          <g>
            <line x1={emit.x} y1={emit.y} x2={hit.x} y2={hit.y}
              stroke={alerted ? RED : GREEN} strokeWidth={2.5} strokeOpacity={0.55} strokeDasharray="6 8" />
            <circle cx={hit.x} cy={hit.y} r={7 + Math.sin(lt * 9) * 2.5} fill={alerted ? RED : GREEN} opacity={0.9} />
            <Rings cx={hit.x} cy={hit.y} t={lt} startT={1.5} period={1.1} maxR={70}
              color={alerted ? RED : GREEN} alpha={0.55} n={2} />
            {alerted && <Rings cx={caneX - 138} cy={232} t={lt} startT={0} color={SOFT} maxR={150} />}
          </g>
        )}
      </svg>
      {beamOn && (
        <Chip x={1240} y={330} o={env(lt, 1.6, 0.6, T.alert - T.down - 0.4, 0.4)}
          tone={alerted ? 'danger' : 'ok'}
          label={alerted ? 'Drop detected' : 'Surface OK'}
          value={alerted ? 'Stop' : 'Level'} />
      )}
      <Cap t0={T.down + 0.7} t1={T.down + 4.4} size={88} y={120} text="Reads the ground ahead." />
      <Cap t0={T.down + 5.6} t1={T.alert - 0.4} size={88} y={120} text="Kerbs. Stairs. Drop-offs." />
    </Frame>
  );
}

// ── Scene 6 · Alerts ────────────────────────────────────────────────
function Alerts() {
  const t = useTime();
  if (t < T.alert - 0.3 || t > T.outro + 0.2) return null;
  const lt = t - T.alert;
  const p = ((lt + 0.4) % 1.4);
  const buzz = Math.sin(lt * 88) * 4.5 * Math.exp(-p * 5) * CL((lt - 0.6) / 0.4, 0, 1);
  const pulse = Math.exp(-p * 5);
  const endLt = T.outro - T.alert - 0.4;
  return (
    <Frame t0={T.alert} t1={T.outro} zoomFrom={1.1} zoomTo={1.02} origin="50% 55%">
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(640px 640px at 50% 58%, rgba(59,130,246,0.11), transparent 70%)' }} />
      {/* Cane placement chosen so the whole stick — grip included — fits the
        * stage; the ring/callout anchors below are the grip centre (900,164)
        * and sensor-module speaker area (966,705) after this wrapper's
        * rotate(-7°) scale(1.15) about its 50% 40% origin. */}
      <div style={{ position: 'absolute', left: 850, top: 100, transform: `translateX(${buzz}px) rotate(-7deg) scale(1.15)`, transformOrigin: '50% 40%' }}>
        <Cane h={780} glow={0.35 + pulse * 0.5} />
      </div>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', left: 0, top: 0 }}>
        <Rings cx={900} cy={164} t={lt} startT={0.6} period={1.4} maxR={150} color={SOFT} alpha={0.7} />
        <Rings cx={966} cy={705} t={lt} startT={1.0} period={1.4} maxR={230} color={ACCENT} alpha={0.55} n={4} />
      </svg>
      <Callout lt={lt} at={1.6} endLt={endLt} x={878} y={164} side="left" title="Haptic pulse" sub="Felt in the grip" />
      <Callout lt={lt} at={2.4} endLt={endLt} x={1026} y={705} side="right" title="Audio alert" sub="Clear, distinct beeps" />
      <Cap t0={T.alert + 3.4} t1={T.outro - 0.4} size={96} y={880} text="Feel it. Hear it. Instantly." />
    </Frame>
  );
}

// ── Scene 7 · Outro ─────────────────────────────────────────────────
function Outro() {
  const t = useTime();
  if (t < T.outro - 0.3) return null;
  const lt = t - T.outro;
  return (
    <Frame t0={T.outro} t1={T.end - 0.05} zoomFrom={1.06} zoomTo={1} fadeOut={0.9}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(700px 520px at 50% 48%, rgba(59,130,246,0.08), transparent 70%)' }} />
      <Radar t={lt} alpha={0.28} cy={520} />
      <Pill t0={T.outro + 0.5} t1={T.end - 0.5} y={300} text="ClearWay" />
      <Cap t0={T.outro + 1.0} t1={T.end - 0.45} size={132} y={392} text="Smart Mobility." />
      <Cap t0={T.outro + 1.7} t1={T.end - 0.45} size={132} y={540} text="Real Confidence." />
      <Cap t0={T.outro + 2.8} t1={T.end - 0.45} size={22} y={764} useGrad={false} color={DIM} weight={500} track="0.02em"
        text="ClearWay Mobility LLP" />
    </Frame>
  );
}

// ── Root ──────────────────────────────────────────────────────────
export default function ClearWayExplainer({ time }) {
  return (
    <TimeContext.Provider value={time}>
      <div style={{ position: 'absolute', inset: 0 }}>
        <Intro />
        <Problem />
        <Reveal />
        <Forward />
        <Downward />
        <Alerts />
        <Outro />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(125% 95% at 50% 45%, transparent 55%, rgba(0,0,0,0.55) 100%)' }} />
      </div>
    </TimeContext.Provider>
  );
}
